import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Image,
  View,
  Text,
  ImageBackground,
  Platform,
  ScrollView,
  StyleSheet,
} from "react-native";
import Toast from "react-native-toast-message";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import RenderHTML from "react-native-render-html";

import BGDay from "./BGDay";
import BGLogo from "./BGLogo";
import SvaraModal from "../SvaraModal";
import BGDayMobile from "./BGDayMobile";
import BGInfoButton from "./BGInfoButton";
import BGSlotGame from "./games/BGSlotGame";
import BGJackpotLabel from "./BGJackpotLabel";
import BGCollectButton from "./BGCollectButton";
import { bonusGamesModalStyles } from "./styles";
import BGScratchGame from "./games/BGScratchGame";
import MiniGameModal from "./games/MiniGameModal";
import BGBlackAndRedGame from "./games/BGBlackAndRed";
import { useDevice } from "../../../../utils/hooks/useDevice";
import {
  BGCollectBtnWrapper,
  BGHorDivider,
  BGTimerWrapper,
  BGVertDividerMobile,
  BtnCloseModal,
} from "../../../../assets";
import { getPlatformUI } from "./BGPlatformUI";
import { bonusInitRequest } from "../../../../store/bonus/bonusActions";
import { miniGamesInitRequest } from "../../../../store/miniGames/miniGamesActions";
import { RootState } from "../../../../store";
import { DayCount } from "./utils";
import Centered from "../../../../shared/Centered";
import { inset } from "../../../../utils/helpers/css";
import { useBonusCountdown } from "../../../../utils/hooks/useBonusCountdown";
import { env } from "../../../../utils/env";
import { profileRequest } from "../../../../store/auth/authActions";
import LS from "../../../../utils/localStorageUtils";

interface IProps {
  onClose: () => void;
}

function BonusGamesModal({ onClose }: IProps) {
  const miniGameConfig = {
    url: env("MINI_GAMES_API_URL") || "",
    token: LS.get("access_token") || "",
    language: LS.get("lang") || "en",
  };

  const { width, isDesktop } = useDevice();
  const [gamePath, setGamePath] = useState<string | null>(null);
  const [showInfoModal, setShowInfoModal] = useState(false);
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const loaded = useSelector((s: RootState) => s.bonus.loaded);
  const collecting = useSelector((s: RootState) => s.bonus.collecting);
  const requestedRef = useRef(false);
  useEffect(() => {
    if (requestedRef.current) {
      console.log("Bonus already requested, skipping...");
      return;
    }
    requestedRef.current = true;
    dispatch(bonusInitRequest());
    dispatch(profileRequest());
    dispatch(miniGamesInitRequest());
  }, []);

  const playerProfile = useSelector((s: RootState) => s.auth.user);
  const bonus = useSelector((s: RootState) => s.bonus.bonus);
  const miniGames = useSelector((s: RootState) => s.miniGames.games);

  const collectedAt = useSelector(
    (s: RootState) => s.auth.user?.collectedAt,
  ) as string | undefined;

  const bonusHours = useSelector((s: RootState) => s.auth.user?.bonusHours) as
    | string
    | undefined;

  const { timeText, ready } = useBonusCountdown(
    collectedAt,
    Number(bonusHours),
  );

  const dailyArray = useMemo(() => {
    if (!bonus || !bonus.bonusAmounts || !Array.isArray(bonus.bonusAmounts)) {
      return [1, 2, 3, 4, 5].map((day) => ({
        day,
        chips: 0,
        vipChips: 0,
      }));
    }

    const vipAmounts = Array.isArray(bonus.vipAmounts) ? bonus.vipAmounts : [];

    return [1, 2, 3, 4, 5].map((day) => ({
      day, // 1..5
      chips: bonus.bonusAmounts[day - 1] ?? 0,
      vipChips: vipAmounts[day - 1] ?? 0,
    }));
  }, [bonus]);

  const currentDay = useSelector((s: RootState) => {
    const prevDays = s.bonus.bonus?.previous_days_collected;
    if (prevDays === undefined || prevDays === null) return undefined;
    // Convert to currentDay (previous_days_collected + 1)
    return prevDays + 1;
  });

  const normalizedCurrentDay: DayCount | undefined = useMemo(() => {
    if (typeof currentDay !== "number") return undefined;

    // currentDay comes from: previous_days_collected + 1
    // So if previous_days_collected = 0, currentDay = 1 (Day 1 claimed)
    // We want to show Day 2 as current

    // If all 5 days collected (currentDay = 5), keep showing Day 5 as current
    if (currentDay >= 5) {
      return 1 as DayCount; // Day 1 is next
    }

    // If no days collected yet (currentDay = 0), show Day 1 as current
    if (currentDay <= 0) {
      return 1 as DayCount;
    }

    // Otherwise, show the next day to collect as current
    const nextDay = currentDay + 1;
    console.log(`Normal progression, next day: ${nextDay}`);
    return nextDay as DayCount;
  }, [currentDay]);

  const { containerScale, isSmall } = useMemo(() => {
    let scale = 1;

    if (width > 1900) scale = width / 700 / 2.1;
    else if (width > 1500) scale = width / 700 / 1.8;
    else if (width > 1200) scale = width / 700 / 1.5;
    else if (width > 850) scale = width / 700 / 1.3;
    else if (width > 760) scale = 0.86;
    else if (width > 650) scale = 0.7;
    else scale = width / 930;

    return { containerScale: scale, isSmall: width < 700 };
  }, [width]);

  const ui = useMemo(
    () => getPlatformUI({ isSmall, containerScale, width, isDesktop }),
    [isSmall, containerScale, width, isDesktop],
  );

  return (
    <>
      <SvaraModal
        visible={!gamePath}
        onClose={onClose}
        closeIcon={BtnCloseModal}
        {...ui.modalProps}
      >
        <BGInfoButton
          isSmall={isSmall}
          onPress={() => setShowInfoModal(true)}
        />

        <View
          style={{
            ...bonusGamesModalStyles.container,
            ...(isSmall ? ui.smallContainerStyle : null),
          }}
        >
          <BGLogo isSmall={isSmall} />
          <BGJackpotLabel isSmall={isSmall} />

          {!isSmall && (
            <>
              <View
                style={{
                  width: "100%",
                  flexDirection: "row",
                  marginTop: -35,
                  marginBottom: 15,
                  gap: 10,
                }}
              >
                <BGBlackAndRedGame
                  onStartGame={setGamePath}
                  config={miniGameConfig}
                  miniGameData={miniGames?.black_and_red}
                  playerLevel={playerProfile?.level || 1}
                />
                <BGScratchGame
                  onStartGame={setGamePath}
                  config={miniGameConfig}
                  miniGameData={miniGames?.scratch_card}
                  playerLevel={playerProfile?.level || 1}
                />
                <BGSlotGame
                  onStartGame={setGamePath}
                  config={miniGameConfig}
                  miniGameData={miniGames?.slot}
                  playerLevel={playerProfile?.level || 1}
                />
              </View>

              <View
                style={{
                  width: "100%",
                  flexDirection: "row",
                  marginBottom: 10,
                  gap: 10,
                }}
              >
                {dailyArray?.map((d) => (
                  <BGDay
                    key={d.day}
                    day={d.day as DayCount}
                    bonus={d.chips}
                    vipBonus={d.vipChips}
                    currentDay={normalizedCurrentDay}
                    isVip={playerProfile?.isVip}
                  />
                ))}

                {ready ? (
                  <BGCollectButton />
                ) : (
                  <>
                    <View style={{ flexDirection: "column" }}>
                      <View
                        style={{
                          alignItems: "center",
                          justifyContent: "center",
                          marginVertical: 10,
                        }}
                      >
                        <Text
                          style={{
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: isSmall ? 14 : 16,
                            fontFamily:
                              Platform.OS === "ios" ? "Arial" : "sans-serif",
                            textTransform: "uppercase",
                            textShadowColor: "rgba(0, 0, 0, 0.98)",
                            textShadowOffset: { width: 0, height: 3 },
                            textShadowRadius: 4,
                            letterSpacing: 0.6,
                            textAlign: "center",
                          }}
                        >
                          {t("T_SITE_BONUSES_HEADING")}
                        </Text>
                      </View>
                      <Image
                        source={BGHorDivider}
                        resizeMode="contain"
                        style={{
                          width: "100%",
                          aspectRatio: 27.5,
                          maxWidth: isSmall ? 220 : undefined,
                        }}
                      />

                      <View style={{ paddingTop: 10 }}>
                        <View
                          style={{
                            width: 125,
                            height: 34,
                            position: "relative",
                            paddingRight: 2,
                            marginBottom: 2,
                          }}
                        >
                          <Image
                            source={BGTimerWrapper}
                            style={{
                              width: "100%",
                              aspectRatio: 3.38,
                              transform: [{ scale: 1.4 }],
                            }}
                          />
                          <Centered
                            style={{ ...inset(0), position: "absolute" }}
                          >
                            <Text
                              style={{
                                color: "white",
                                fontWeight: "900",
                                fontSize: 13,
                                width: "100%",
                                textAlign: "center",
                              }}
                              numberOfLines={1}
                              ellipsizeMode="tail"
                            >
                              {timeText}
                            </Text>
                          </Centered>
                        </View>
                      </View>
                    </View>
                  </>
                )}
              </View>
            </>
          )}

          {isSmall && (
            <View
              style={{
                width: "100%",
                marginTop: -10,
                marginBottom: 8,
                position: "relative",
              }}
            >
              <View
                style={{ position: "absolute", left: "59%", top: 0, bottom: 0 }}
              >
                <Image
                  style={{ height: "100%", aspectRatio: 20 / 1344 }}
                  source={BGVertDividerMobile}
                />
              </View>

              <View style={{ width: "100%", flexDirection: "row" }}>
                <View style={{ width: "60%", paddingHorizontal: 10 }}>
                  <BGBlackAndRedGame
                    isSmall
                    onStartGame={setGamePath}
                    config={miniGameConfig}
                    miniGameData={miniGames?.black_and_red}
                    playerLevel={playerProfile?.level || 1}
                  />
                  <BGScratchGame
                    isSmall
                    onStartGame={setGamePath}
                    config={miniGameConfig}
                    miniGameData={miniGames?.scratch_card}
                    playerLevel={playerProfile?.level || 1}
                  />
                  <BGSlotGame
                    isSmall
                    onStartGame={setGamePath}
                    config={miniGameConfig}
                    miniGameData={miniGames?.slot}
                    playerLevel={playerProfile?.level || 1}
                  />
                </View>

                <View
                  style={{
                    width: "40%",
                    paddingHorizontal: 10,
                    alignItems: "center",
                  }}
                >
                  {ready ? (
                    <BGCollectButton isSmall />
                  ) : (
                    <>
                      <Image
                        source={BGHorDivider}
                        resizeMode="contain"
                        style={{
                          width: "100%",
                          aspectRatio: 27.5,
                          maxWidth: isSmall ? 220 : undefined,
                        }}
                      />
                      <View
                        style={{
                          alignItems: "center",
                          justifyContent: "center",
                          marginVertical: 1,
                        }}
                      >
                        <Text
                          style={{
                            color: "#fff",
                            fontWeight: "bold",
                            fontSize: isSmall ? 14 : 16,
                            fontFamily:
                              Platform.OS === "ios" ? "Arial" : "sans-serif",
                            textTransform: "uppercase",
                            textShadowColor: "rgba(0, 0, 0, 0.98)",
                            textShadowOffset: { width: 0, height: 3 },
                            textShadowRadius: 4,
                            letterSpacing: 0.6,
                            textAlign: "center",
                          }}
                        >
                          {t("T_SITE_BONUSES_HEADING")}
                        </Text>
                      </View>
                      <Image
                        source={BGHorDivider}
                        resizeMode="contain"
                        style={{
                          width: "100%",
                          aspectRatio: 27.5,
                          maxWidth: isSmall ? 220 : undefined,
                        }}
                      />
                      {ui.renderSmallTimer({ timeText })}
                    </>
                  )}

                  {Platform.OS === "web" ? null : (
                    <View style={{ marginTop: 4 }} />
                  )}
                  {(Platform.OS === "web"
                    ? dailyArray?.slice(0, 5)
                    : dailyArray?.slice(0, 5)
                  ).map((d) => (
                    <BGDayMobile
                      key={d.day}
                      day={d.day as DayCount}
                      bonus={d.chips}
                      vipBonus={d.vipChips}
                      currentDay={normalizedCurrentDay}
                      isSmall={isSmall}
                      isVip={playerProfile?.isVip}
                    />
                  ))}
                </View>
              </View>
            </View>
          )}
        </View>

        {/* Toast inside modal so it shows above content on native/mobile */}
        {(Platform.OS !== "web" || !isDesktop) && (
          <View style={styles.toastInModal} pointerEvents="box-none">
            <Toast position="bottom" bottomOffset={10} />
          </View>
        )}
      </SvaraModal>

      {gamePath && (
        <MiniGameModal
          width={width}
          gamePath={gamePath}
          onClose={() => {
            setGamePath(null);
            dispatch(miniGamesInitRequest());
            onClose();
          }}
        />
      )}

      <SvaraModal
        visible={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        closeIcon={BtnCloseModal}
        closeButtonSize="big"
        closeButtonStyles={{
          position: "absolute",
          zIndex: 100,
          width: 48,
          height: 48,
          top: isSmall ? -23 : -23,
          right: isSmall ? -2 : 20,
          transform: [{ scale: isSmall ? 1 : 1 }],
        }}
        containerStyles={{
          maxWidth: isDesktop ? 800 : width * 0.9,
          marginTop: isSmall ? 30 : 60,
        }}
        overlayStyle={{
          backgroundColor: "rgba(0,0,0,0.9)",
        }}
      >
        <ScrollView
          style={{ maxHeight: 500 }}
          contentContainerStyle={{
            paddingHorizontal: isDesktop ? 40 : 20,
            paddingTop: 20,
            paddingBottom: 40,
            maxWidth: isDesktop ? 800 : "100%",
          }}
          showsVerticalScrollIndicator
          nestedScrollEnabled
        >
          <RenderHTML
            contentWidth={isDesktop ? 800 : width * 0.8}
            source={{ html: t("T_SITE_MODAL_BONUS_INFO") }}
            tagsStyles={{
              div: {
                color: "#fff",
                fontSize: 16,
              },
              p: {
                color: "#fff",
                fontSize: 16,
                marginBottom: 12,
              },
              h1: {
                color: "#fff",
                fontSize: 24,
                fontWeight: "bold",
                marginBottom: 16,
              },
              h2: {
                color: "#fff",
                fontSize: 20,
                fontWeight: "bold",
                marginBottom: 12,
              },
              h3: {
                color: "#fff",
                fontSize: 18,
                fontWeight: "bold",
                marginBottom: 10,
              },
              ul: {
                color: "#fff",
                marginBottom: 12,
              },
              li: {
                color: "#fff",
                fontSize: 16,
                marginBottom: 8,
              },
              strong: {
                color: "#fff",
                fontWeight: "bold",
              },
            }}
          />
        </ScrollView>
      </SvaraModal>
    </>
  );
}

const styles = StyleSheet.create({
  toastInModal: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 999999,
    elevation: 999999,
  },
});

export default BonusGamesModal;
